import React from 'react';
import { ModalContainer, ModalExitIcon } from 'shared/modals/v2-container';
import styled, { css } from 'styled-components';

import { ModalWrapper } from 'shared/modals/v2-wrapper';
import { ModalContent as _ModalContent } from 'components/modals/components';
import useTranslation from 'hooks/use-translation';
import Image from 'next/image';

import useCart from 'hooks/use-cart';
import { DutchiePayLogo as _DutchiePayLogo } from 'assets/dutchie-pay-logo';
import { PlusIcon } from 'assets/plus';
import { Text } from 'rebass';
import { DutchiePayConfirmationButton } from 'dutchie-pay/components';
import { CheckIcon } from 'assets/check';
import { useRouter } from 'next/router';
import Imgix from 'shared/components/imgix';
import { ImageOrientation, useGetImageOrientation } from 'hooks/use-get-image-orientation';
import { tracker } from 'utils/analytics';
import useLogRocket from 'src/hooks/use-logrocket';
import { ModalObserver } from '../modal-observer';

export const DutchiePayCtaModal = ({ name }: { name: string }): JSX.Element => {
  const { t } = useTranslation();
  const cart = useCart();

  const router = useRouter();
  const dispensaryLogo = cart?.dispensary?.SpecialLogoImage;
  const { imageOrientation } = useGetImageOrientation(dispensaryLogo);
  const { track } = useLogRocket();

  const onCtaClick = (): void => {
    // TODO: Setup LogRocket in the FE Analytics framework https://dutchie.atlassian.net/browse/ENG-31004
    tracker.dutchiePayCTAButtonClicked();
    track(`DutchiePay CTA Button Clicked`);

    void router.push('/dutchiepay');
  };

  return (
    <ModalObserver name={name}>
      {(props) => (
        <ModalWrapper {...props}>
          <ModalContainer data-cy='dutchie-pay-cta-modal' width={365} padding={0}>
            <ModalExitIcon onClick={props.onClose} />
            <ModalContent>
              <HeaderContainer>
                <DispensaryLogo src={dispensaryLogo} alt='logo' orientation={imageOrientation} />
                <PlusIconContainer>
                  <PlusIcon />
                </PlusIconContainer>
                <DutchiePayLogo width='110' height='20' />
              </HeaderContainer>
              <Heading>{t('dutchiePay.ditchTheCash', 'Ditch the cash')}</Heading>
              <BodyText>
                {t('dutchiePay.linkYourBank', 'Link to your bank for simple and secure payments for cannabis.')}
              </BodyText>
              <ContentContainer>
                <List>
                  <Item>
                    <CheckIcon />
                    {t('dutchiePay.easySetup', 'Easy setup')}
                  </Item>
                  <Item>
                    <CheckIcon />
                    {t('dutchiePay.fastCheckouts', 'Fast checkouts')}
                  </Item>
                  <Item>
                    <CheckIcon />
                    {t('dutchiePay.avoidAtmFees', 'Avoid ATM fees')}
                  </Item>
                </List>
                <Image src='/images/mx.png' width={113} height='auto' />
              </ContentContainer>
              <Button onClick={onCtaClick}>{t('common.signup', 'Sign Up')}</Button>
            </ModalContent>
          </ModalContainer>
        </ModalWrapper>
      )}
    </ModalObserver>
  );
};

const DispensaryLogo = styled(Imgix)`
  max-height: ${(props) => (props.orientation === ImageOrientation.vertical ? '32px' : '56px')};
`;

const PlusIconContainer = styled.div`
  opacity: 0.2;
  margin: 0 4px 0 8px;
`;

const DutchiePayLogo = styled(_DutchiePayLogo)`
  ${({ theme: { colors } }) => css`
    color: ${colors.dutchiePayGreen};
  `}
`;
const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 25px;
`;

const ContentContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 25px 0 15px 0;
  padding: 0 15px 0 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey[90]};
`;

const Heading = styled.h2`
  font-size: 26px;
  line-height: 31px;
  text-align: left;
  margin: 20px 0 5px 0;
`;

const List = styled.div`
  text-align: left;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
  margin-top: -16px;
`;

const Item = styled(Text)`
  font-size: 16px;
  display: flex;
  gap: 8px;
  align-items: center;
`;

const BodyText = styled(Text)`
  text-align: left;
  font-size: 16px;
  line-height: 22px;
`;

const Button = styled(DutchiePayConfirmationButton)`
  font-size: 16px;
`;

const ModalContent = styled(_ModalContent)`
  padding: 20px 24px;
`;
